import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ProducerInterface from '@app/Interface/ProducerInterface';
import ProducerModel from '@app/Model/ProducerModel';
import UserAuthentication from '@app/Security/UserAuthentication';
import UserModel from '@app/Model/UserModel';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import { ActionGroup, Button, Checkbox, DescriptionList, DescriptionListDescription, DescriptionListGroup, DescriptionListTerm, Form, FormGroup, Modal, ModalVariant, PageSection, Title } from '@patternfly/react-core';
import UserRoleTypesInterface from '@app/Interface/UserRoleTypesInterface';
import MyFpuListFunctions from '@app/Page/MyFpuListPage/MyFpuListFunctions';
import UserRoleDescriptions from '@app/Components/User/UserRoleDescriptions';
import ToastNotificationInterface, { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import ToastNotifications from '@app/Components/ToastNotification/ToastNotifications';
import UserFpuAccessHelper from '@app/Service/UserFpuAccessHelper';
import FpuPermissionsComponentTypeInterface from '@app/Interface/FpuPermissionsComponentViewInterface';
import FpuPermissionsComponentTypeModel from '@app/Model/FpuPermissionsComponentViewModel';
import { PermissionDenied } from '@app/PermissionDenied/PermissionDenied';
import User from '@app/Interface/UserInterface';

interface FpuUpdateUserPermissionsProps {
    userToUpdate: User;
    roleTypes: UserRoleTypesInterface[]
    producerId: number;
    producer: ProducerInterface;
    changeView: (component: FpuPermissionsComponentTypeInterface, fetchUsers?: boolean, toastData?: ToastNotificationInterface) => void;
}

const FpuUpdateUserPermissions: React.FC<FpuUpdateUserPermissionsProps> = ({ userToUpdate, roleTypes, producerId, producer, changeView }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const { userState } = React.useContext(UserContext);

    // NOTE: if user permission was changed, they would need to log out/log in to see changes.
    const canViewPage = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, TemplateDefinitions.TEMPLATE_VIEW_FPU_LIST_PAGE);

    const initialRoleTypes: UserRoleTypesInterface[] = [];

    const [userRoleTypes, setUserRoleTypes] = React.useState(initialRoleTypes);

    const [originalRoleTypes, setOriginalRoleTypes] = React.useState([...initialRoleTypes]);

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const [refreshUserList, setRefreshUserList] = React.useState(false);

    const handleModalToggle = () => {
        setIsModalOpen(!isModalOpen);
    }

    const handleCheckBoxToggle = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {

        const roleId = event.currentTarget.value ?? 0;

        const updatedRoleTypes = [...roleTypes];

        const roleTypeToUpdate = updatedRoleTypes.find(roleType => roleType.id === Number(roleId));

        if (roleTypeToUpdate) {

            // since the user was updated, we need to refresh the user list
            // TODO: Optional, to improve speed and prevent loading users, we could update the User Permissions by calling a function up the chain
            setRefreshUserList(true);

            roleTypeToUpdate.checked = checked;

            setUserRoleTypes(updatedRoleTypes);

        }

    }

    const handleSubmit = async (e: React.SyntheticEvent) => {

        e.preventDefault();

        setIsFormProcessing(true);

        let notificationType: ToastNotificationType = ToastNotificationType.Default;

        let toastTitle = "";

        let tastDescription = "";

        const accessData = MyFpuListFunctions.getUserAccessData(producerId, userToUpdate, userRoleTypes);

        const responseCode = await UserFpuAccessHelper.updateUserRoleAccess(accessData);

        switch (responseCode) {

            case 201:
                notificationType = ToastNotificationType.Success;
                toastTitle = "Success";
                tastDescription = "Account was successfully saved.";

                break;

            case 400:
                notificationType = ToastNotificationType.Danger;
                toastTitle = "Invalid input";
                tastDescription = "Please try again or contact support if the issue persists.";
                break;

            case 404:
                notificationType = ToastNotificationType.Danger;
                toastTitle = "Resource not found";
                tastDescription = "Endpoint to save user is invalid, try again or contact support.";
                break;

            case 422:
                notificationType = ToastNotificationType.Danger;
                toastTitle = "Bad user data";
                tastDescription = "Updated data error";
                break;

            default:
                notificationType = ToastNotificationType.Warning;
                toastTitle = "Unknown error";
                tastDescription = "Unable to save user data, contact support.";
                break;

        }

        CreateToastNotification(toastDispatch, notificationType, toastTitle, tastDescription);

        setIsFormProcessing(false);

    }

    /**
     * Resets to previous values
     * @param e 
     */
    const handleReset = (e: React.FormEvent<HTMLButtonElement>) => {

        e.preventDefault();

        setUserRoleTypes([...originalRoleTypes]);

    }

    // get a list of user role types
    React.useEffect(() => {

        const isUserMember = UserAuthentication.userIsMember(userState.user.viewPermissions);

        const isNonMemberRole = !isUserMember;

        /**
         * fpu roles only 
         * ignore master account 
         */
        const filteredRoles: UserRoleTypesInterface[] = roleTypes.filter(roleType => roleType.roleType === "fpu" && roleType.isMasterAccountRole === false && roleType.isNonMemberRole === isNonMemberRole);

        // determine check box values and inactive state
        // First rule, a role must allow being granted access
        // non-member users can't change member roles
        // member users can't change non-member roles
        // NOTE: The roles should have been filtered so the logged in user can't see roles they don't have access instead of setting inactive state
        const updatedRoleTypes = MyFpuListFunctions.setRoleTypesCheckedStatus(userToUpdate, producerId, filteredRoles, isUserMember);

        console.log("userToUpdate: ", userToUpdate);

        console.log("updatedRoleTypes: ", updatedRoleTypes);

        // need deep copy. using [...] is a shallow copy
        const originalRoleTypes = JSON.parse(JSON.stringify(updatedRoleTypes));

        setUserRoleTypes(updatedRoleTypes);

        setOriginalRoleTypes(originalRoleTypes);

    }, [producerId, roleTypes, userState.user.viewPermissions, userToUpdate]);

    React.useEffect(() => {

        return () => {

            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    if (canViewPage) {

        let displayName = "n/a";

        if (userToUpdate.firstName && userToUpdate.lastName) {
            displayName = `${userToUpdate.firstName} ${userToUpdate.lastName}`;
        }

        let userAddress: JSX.Element | null = null;

        if (userToUpdate.address.length && userToUpdate.city.length & userToUpdate.state.length && userToUpdate.zip.length) {

            userAddress = (<div>
                {userToUpdate.address}<br />
                {userToUpdate.address2.length ? <>{userToUpdate.address2}</> : null}
                {userToUpdate.city}, {userToUpdate.state} {userToUpdate.zip}
            </div>);

        }

        return (
            <ToastNotifications>
                <PageSection>
                    <Button
                        variant="primary"
                        onClick={() => changeView(new FpuPermissionsComponentTypeModel(0, new ProducerModel(), 0, new UserModel(), "FpuPermissions"), refreshUserList)}
                    >
                        Return to User List
                    </Button>
                    <Title headingLevel="h1"className="pf-u-mb-md">Update FPU Permissions</Title>

                    <Title headingLevel="h2">{producer.alphaName} - {producerId}</Title>
                    <DescriptionList isHorizontal>
                        <DescriptionListGroup>
                            <DescriptionListTerm>Name</DescriptionListTerm>
                            <DescriptionListDescription>{displayName}</DescriptionListDescription>
                        </DescriptionListGroup>
                        <DescriptionListGroup>
                            <DescriptionListTerm>Email</DescriptionListTerm>
                            <DescriptionListDescription>{userToUpdate.email}</DescriptionListDescription>
                        </DescriptionListGroup>
                        {userAddress !== null &&
                            <DescriptionListGroup>
                                <DescriptionListTerm>Address</DescriptionListTerm>
                                <DescriptionListDescription>{userAddress}</DescriptionListDescription>
                            </DescriptionListGroup>
                        }
                    </DescriptionList>
                    <Form>
                        <FormGroup fieldId="permission-checkbox-group" label="User Role">
                            <Button variant="link" onClick={handleModalToggle} className="pf-u-p-0">Learn more about permissions</Button>
                            <Modal
                                variant={ModalVariant.medium}
                                title="Permissions"
                                isOpen={isModalOpen}
                                onClose={handleModalToggle}
                                actions={[
                                    <Button key="ok" variant="primary" onClick={handleModalToggle}>
                                        OK
                                    </Button>
                                ]}><UserRoleDescriptions user={userState.user} roleTypes={userRoleTypes} /></Modal>

                            {userRoleTypes.map(roleType => (
                                <Checkbox
                                    key={roleType.id}
                                    label={roleType.title}
                                    id={`roleid_${roleType.id}`}
                                    name={`roleid_${roleType.id}`}
                                    aria-label={roleType.title}
                                    isChecked={roleType.checked}
                                    isDisabled={!roleType.enabled}
                                    onChange={handleCheckBoxToggle}
                                    value={roleType.id}
                                />
                            ))}
                        </FormGroup>
                        <ActionGroup>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                spinnerAriaValueText={isFormProcessing ? "Saving" : undefined}
                                isLoading={isFormProcessing}
                            >{isFormProcessing ? "Saving" : "Submit"}</Button>
                            <Button variant="link" onClick={handleReset}>Reset</Button>
                        </ActionGroup>
                    </Form>
                </PageSection>
            </ToastNotifications>
        );

    } else {

        return (
            <PermissionDenied />
        );

    }

}

export default FpuUpdateUserPermissions;
